import React, { useState } from 'react';
import styled from 'styled-components';

const EditorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Toolbar = styled.div`
  display: flex;
  gap: 4px;
  padding: 8px;
  background: #f5f5f5;
  border-radius: 8px 8px 0 0;
  border: 1px solid #ddd;
  border-bottom: none;
  flex-wrap: wrap;
`;

const ToolbarButton = styled.button`
  padding: 6px 10px;
  background: ${props => props.active ? '#4A90E2' : 'white'};
  color: ${props => props.active ? 'white' : '#333'};
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: ${props => props.bold ? 'bold' : props.italic ? 'italic' : 'normal'};
  transition: all 0.2s;

  &:hover {
    background: ${props => props.active ? '#3a7bc8' : '#f0f0f0'};
  }

  &:active {
    transform: scale(0.95);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 150px;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 0 0 8px 8px;
  font-family: inherit;
  font-size: 0.95rem;
  resize: vertical;
  background: white;

  &:focus {
    outline: none;
    border-color: #4A90E2;
    box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.1);
  }
`;

const PreviewContainer = styled.div`
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: white;
  min-height: 100px;
  color: #333;

  strong {
    font-weight: bold;
  }

  em {
    font-style: italic;
  }

  ul, ol {
    margin: 8px 0;
    padding-left: 24px;
  }

  li {
    margin: 4px 0;
  }

  table {
    border-collapse: collapse;
    margin: 12px 0;
    width: 100%;
  }

  table.bordered th,
  table.bordered td {
    border: 1px solid #ddd;
    padding: 8px;
    text-align: left;
  }

  table.borderless th,
  table.borderless td {
    border: none;
    padding: 8px 12px 8px 0;
    text-align: left;
  }

  th {
    background-color: #f5f5f5;
    font-weight: 600;
  }
`;

const TableModal = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  min-width: 300px;
`;

const TableModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
`;

const TableModalLabel = styled.label`
  display: block;
  margin: 12px 0 4px;
  font-size: 0.9rem;
  font-weight: 600;
  color: #333;
`;

const TableModalInput = styled.input`
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.9rem;
`;

const TableModalCheckbox = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 12px 0;
  cursor: pointer;

  input {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }
`;

const TableModalButtons = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 16px;
  justify-content: flex-end;
`;

const TableModalButton = styled.button`
  padding: 8px 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: ${props => props.primary ? '#4A90E2' : 'white'};
  color: ${props => props.primary ? 'white' : '#333'};
  cursor: pointer;
  font-size: 0.9rem;

  &:hover {
    background: ${props => props.primary ? '#3a7bc8' : '#f0f0f0'};
  }
`;

const PreviewToggle = styled.button`
  padding: 6px 12px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.85rem;

  &:hover {
    background: #f0f0f0;
  }
`;

const RichTextEditor = ({ value, onChange, placeholder }) => {
  const [showPreview, setShowPreview] = useState(false);
  const [showTableModal, setShowTableModal] = useState(false);
  const [tableRows, setTableRows] = useState('3');
  const [tableCols, setTableCols] = useState('3');
  const [tableBorderless, setTableBorderless] = useState(false);
  const textareaRef = React.useRef(null);

  const insertMarkdown = (before, after = before) => {
    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);
    const newText = value.substring(0, start) + before + selectedText + after + value.substring(end);

    onChange({ target: { value: newText } });

    // Restore cursor position
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + before.length, end + before.length);
    }, 0);
  };

  const insertList = (type) => {
    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);

    const lines = selectedText.split('\n').filter(l => l.trim());
    let newList;

    if (type === 'ul') {
      newList = lines.map(line => `- ${line.trim()}`).join('\n');
    } else {
      newList = lines.map((line, i) => `${i + 1}. ${line.trim()}`).join('\n');
    }

    const newText = value.substring(0, start) + newList + value.substring(end);
    onChange({ target: { value: newText } });
  };

  const insertTable = () => {
    const rows = parseInt(tableRows) || 2;
    const cols = parseInt(tableCols) || 2;
    const borderClass = tableBorderless ? 'borderless' : 'bordered';
    
    let tableHTML = `\n<table class="${borderClass}">\n`;
    
    // Header row
    tableHTML += '  <tr>\n';
    for (let c = 0; c < cols; c++) {
      tableHTML += `    <th>Header ${c + 1}</th>\n`;
    }
    tableHTML += '  </tr>\n';
    
    // Data rows
    for (let r = 0; r < rows - 1; r++) {
      tableHTML += '  <tr>\n';
      for (let c = 0; c < cols; c++) {
        tableHTML += `    <td>Cell ${r + 1}-${c + 1}</td>\n`;
      }
      tableHTML += '  </tr>\n';
    }
    
    tableHTML += '</table>\n';
    
    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const newText = value.substring(0, start) + tableHTML + value.substring(start);
    onChange({ target: { value: newText } });
    
    setShowTableModal(false);
    setTableRows('3');
    setTableCols('3');
    setTableBorderless(false);
    
    setTimeout(() => textarea.focus(), 0);
  };

  const renderPreview = (text) => {
    if (!text) return <em style={{ color: '#999' }}>Rien à prévisualiser</em>;

    let html = text;

    // Bold
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

    // Italic (but not inside tables or already formatted)
    html = html.replace(/(?<!<[^>]*)\*([^*<>]+)\*(?![^<]*>)/g, '<em>$1</em>');

    // Unordered lists (not inside tables)
    const lines = html.split('\n');
    const processed = [];
    let inList = false;
    let inTable = false;

    for (const line of lines) {
      if (line.trim().startsWith('<table')) inTable = true;
      if (line.trim().startsWith('</table>')) inTable = false;

      if (!inTable && line.trim().startsWith('- ')) {
        if (!inList) {
          processed.push('<ul>');
          inList = true;
        }
        processed.push(`<li>${line.substring(2)}</li>`);
      } else if (!inTable && /^\d+\.\s+/.test(line.trim())) {
        const match = line.match(/^\d+\.\s+(.+)$/);
        if (match) {
          processed.push(`<li>${match[1]}</li>`);
        }
      } else {
        if (inList && !inTable) {
          processed.push('</ul>');
          inList = false;
        }
        processed.push(line);
      }
    }

    if (inList) processed.push('</ul>');
    html = processed.join('<br />');

    // Remove double breaks around tables
    html = html.replace(/<br \/><table/g, '<table');
    html = html.replace(/<\/table><br \/>/g, '</table>');

    return <div dangerouslySetInnerHTML={{ __html: html }} />;
  };

  return (
    <EditorContainer>
      <Toolbar>
        <ToolbarButton
          type="button"
          bold
          onClick={() => insertMarkdown('**', '**')}
          title="Gras (Ctrl+B)"
        >
          <strong>B</strong>
        </ToolbarButton>

        <ToolbarButton
          type="button"
          italic
          onClick={() => insertMarkdown('*', '*')}
          title="Italique (Ctrl+I)"
        >
          <em>I</em>
        </ToolbarButton>

        <ToolbarButton
          type="button"
          onClick={() => insertList('ul')}
          title="Liste à puces"
        >
          • Liste
        </ToolbarButton>

        <ToolbarButton
          type="button"
          onClick={() => insertList('ol')}
          title="Liste numérotée"
        >
          1. Liste
        </ToolbarButton>

        <ToolbarButton
          type="button"
          onClick={() => setShowTableModal(true)}
          title="Insérer un tableau"
        >
          ⊞ Tableau
        </ToolbarButton>

        <div style={{ flex: 1 }} />

        <PreviewToggle
          type="button"
          onClick={() => setShowPreview(!showPreview)}
        >
          {showPreview ? '✏️ Éditer' : '👁️ Prévisualiser'}
        </PreviewToggle>
      </Toolbar>

      {/* Table Modal */}
      {showTableModal && (
        <>
          <TableModalOverlay onClick={() => setShowTableModal(false)} />
          <TableModal onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '1.1rem' }}>Insert Table</h3>
            
            <TableModalLabel>Number of Rows:</TableModalLabel>
            <TableModalInput
              type="number"
              min="2"
              max="20"
              value={tableRows}
              onChange={(e) => setTableRows(e.target.value)}
            />

            <TableModalLabel>Number of Columns:</TableModalLabel>
            <TableModalInput
              type="number"
              min="2"
              max="10"
              value={tableCols}
              onChange={(e) => setTableCols(e.target.value)}
            />

            <TableModalCheckbox>
              <input
                type="checkbox"
                checked={tableBorderless}
                onChange={(e) => setTableBorderless(e.target.checked)}
              />
              <span>Invisible borders (borderless)</span>
            </TableModalCheckbox>

            <TableModalButtons>
              <TableModalButton onClick={() => setShowTableModal(false)}>
                Cancel
              </TableModalButton>
              <TableModalButton primary onClick={insertTable}>
                Insert Table
              </TableModalButton>
            </TableModalButtons>
          </TableModal>
        </>
      )}

      {showPreview ? (
        <PreviewContainer>
          {renderPreview(value)}
        </PreviewContainer>
      ) : (
        <TextArea
          ref={textareaRef}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
        />
      )}
    </EditorContainer>
  );
};

export default RichTextEditor;
